import type { Package, SavedCard } from '@aslgo/shared';
import { ModalOverlay } from '../components/ModalOverlay.js';

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className={strong ? 'font-semibold text-ink-900 dark:text-ink-100' : 'text-ink-600 dark:text-ink-400'}>{label}</span>
      <span className={strong ? 'font-semibold text-ink-900 dark:text-ink-100' : 'text-ink-800 dark:text-ink-200'}>{value}</span>
    </div>
  );
}

const brandName = (b: string) => (b ? b.charAt(0).toUpperCase() + b.slice(1) : 'Card');

/** In-app confirmation shown before charging the learner's saved card (replaces
 *  a native window.confirm). Presents an order summary — package, minutes, price,
 *  and the card being charged — with Cancel / Pay actions. */
export function ConfirmPurchaseDialog({
  pkg, card, busy, onConfirm, onCancel,
}: {
  pkg: Package;
  card: SavedCard;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const price = `$${pkg.priceDollars.toLocaleString()}`;
  return (
    <ModalOverlay
      role="dialog"
      aria-modal="true"
      aria-label="Confirm purchase"
      className="z-50 bg-ink-900/60"
    >
      <div className="max-w-sm w-[90%] mx-auto rounded-2xl bg-canvas-light dark:bg-canvas-dark p-6 shadow-xl ring-1 ring-ink-900/10 dark:ring-white/10">
        <h2 className="h-card text-ink-900 dark:text-ink-100 mb-1">Confirm purchase</h2>
        <p className="hint mb-4">Review your order — we'll charge your saved card.</p>

        <div className="rounded-xl border border-ink-200 dark:border-white/10 divide-y divide-ink-200 dark:divide-white/10 mb-5">
          <Row label={pkg.name} value={price} strong />
          <Row label="Minutes added" value={`${pkg.minutes} min`} />
          <Row label="Payment" value={`${brandName(card.brand)} ·· ${card.last4}`} />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Processing…' : `Pay ${price}`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
